import {
  Alert,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useEffect, useRef, useState } from 'react';
import {
  IconCards,
  IconDice,
  IconHistory,
  IconPlayerPause,
  IconAward,
  IconSunHigh,
  IconTrash,
} from '@tabler/icons-react';
import {
  DEMO_FINAL_COPY,
  DEMO_SOLO_TASK_REWARDS,
  LANE_CAPACITY,
  MASTER_SEAL_TITLE,
  MONEY_STEP,
  PLAYERS_COUNT_LABEL,
  RESOURCE_KEYS,
  RESOURCE_GOAL_MESSAGE,
  formatSessionDeletion,
  isPlayerKind,
  sessionDeletionAt,
  type EventCardAward,
  type GameSessionView,
  type ResourceKey,
} from '@creativitas/shared';
import { MoneyCounter, ResourceCounter } from '@/components/domain/ResourceCounter';
import { ParticipantItem } from '@/components/domain/ParticipantChip';
import type { RealtimeStatus } from '@/features/spreads/useSessionRealtime';
import { useDiceRoll } from './BoardDice';
import { GameBoard } from './GameBoard';
import { GameIntro, MasterSeal, type GameIntroActions } from './GameIntro';
import { HostActionsPanel, type HostActions } from './HostActionsPanel';
import { GameCardsPanel } from './GameCardsPanel';
import { gameTurnPresentation } from './game-turn';
import { DemoCoach, pickCoach, useDemoCoach } from './demo/DemoCoach';
import { DemoClosedSectors, DemoFinal } from './demo/DemoFinal';
import { DemoGuidance, DemoTurnPanel } from './demo/DemoTurnPanel';
import {
  demoStoryGuidance,
  describeDemoStep,
  type DemoAction,
  type DemoViewer,
} from './demo/demo-step';

export type GameWorkspaceActions = GameIntroActions & HostActions & {
  roll: (participantId: string) => void;
  draw: (participantId: string) => void;
  reveal: (cardId: string) => void;
  resource?: (participantId: string, resource: ResourceKey, delta: -1 | 1) => void;
  money?: (participantId: string, delta: number) => void;
  remove?: (participantId: string) => void;
  /** GAME-39 — ведущий закрывает карту события после ответа. */
  resolveCard?: (cardId: string, award: EventCardAward) => void;
  /** Демо «для себя» — игрок отмечает выполненное задание. */
  confirm?: (cardId: string) => void;
  /** Демо «как ведущий» — начать партию и сесть за стол из панели хода. */
  start?: () => void;
  seat?: () => void;
};

export function GameWorkspace({
  session,
  viewerParticipantId,
  facilitator,
  busy,
  actions,
  demoViewer,
  coachKey = 'demo-coach',
  connection,
}: {
  session: GameSessionView;
  viewerParticipantId?: string;
  facilitator: boolean;
  busy: boolean;
  actions: GameWorkspaceActions;
  /** Кто смотрит на демопартию. У партии полной игры его нет. */
  demoViewer?: DemoViewer;
  /** Где хранится прогресс обучения: по пользователю и сценарию. */
  coachKey?: string;
  connection?: RealtimeStatus;
}) {
  const demo = session.demo && demoViewer ? session.demo : null;
  const players = session.participants.filter((participant) => isPlayerKind(participant.kind));
  const current = players.find((participant) => participant.id === session.currentTurnParticipantId);
  const effectParticipant = players.find((participant) => participant.id === session.lastDice?.participantId) ?? current;
  const { latestRoll, completedCardDraws, pendingCardDraws, participantId: displayedTurnId } = gameTurnPresentation(session);
  const displayedPlayer = players.find((participant) => participant.id === displayedTurnId);
  /*
    The cube on the board is driven by the log's sequence number, not by the
    value: two throws in a row can both come up four, and the second one still
    has to tumble. Keyed this way it also animates for everyone in the room,
    not only for whoever pressed the button.
  */
  const dice = useDiceRoll(session.lastDice?.value ?? null, latestRoll?.seq ?? null);
  /*
    The cube is on the table only while the throw is what the turn is about: it
    arrives with the first throw of the party, leaves once the first card of that
    throw is drawn, and comes back with the next throw. `rolling` is checked too,
    because the press has to bring the cube back before the server confirms.
  */
  const thrown = Boolean(latestRoll) || dice.phase === 'rolling';
  const diceVisible = thrown && (completedCardDraws === 0 || dice.phase === 'rolling');

  /*
    GAME-34: «вы собрали все шесть ресурсов и успешно прошли игру».
    
    The set starts out holding whoever had already finished their ray when this
    window opened, so a reload or a late join does not re-announce an
    achievement from ten minutes ago — only a ray filling while somebody is
    watching opens the notice. The server's timestamp is what makes it fire
    once; this ref only decides which windows say it out loud.
  */
  const announcedGoals = useRef<Set<string> | null>(null);
  const goalMessage = demo ? DEMO_FINAL_COPY.rayFull : RESOURCE_GOAL_MESSAGE;
  useEffect(() => {
    const reached = session.participants.filter((item) => item.resourceGoalReachedAt !== null);
    if (announcedGoals.current === null) {
      announcedGoals.current = new Set(reached.map((item) => item.id));
      return;
    }
    const seen = announcedGoals.current;
    for (const participant of reached) {
      if (seen.has(participant.id)) continue;
      seen.add(participant.id);
      const isViewer = participant.id === viewerParticipantId;
      modals.open({
        title: isViewer ? 'Все шесть ресурсов собраны' : `${participant.displayName} — шесть ресурсов`,
        children: (
          <Stack gap="sm">
            <Text>{isViewer ? goalMessage : `${participant.displayName}: ${goalMessage}`}</Text>
            {/* Дарья: «Выиграли в игре, но можете продолжить» — путь не обрывается. */}
            {demo ? null : (
              <Text size="sm" c="var(--text-muted)">
                Игра продолжается: дорожка заполнена, а ходы и карты остаются доступны до Маяка.
              </Text>
            )}
          </Stack>
        ),
      });
    }
  }, [session.participants, viewerParticipantId, goalMessage, demo]);

  /*
    GAME-38: «Печать Мастера» opens by itself for the player who has just
    reached the Lighthouse, and can be reopened from their card afterwards.
    Seeded from the finished players present at mount, so a reload does not
    replay somebody else's finish.
  */
  const [sealFor, setSealFor] = useState<string | null>(null);
  const announcedFinishes = useRef<Set<string> | null>(null);
  useEffect(() => {
    /* В демо «Печати Мастера» нет: путь кончается итогом трёх секторов. */
    if (session.demo) return;
    const finished = session.participants.filter((item) => item.finished);
    if (announcedFinishes.current === null) {
      announcedFinishes.current = new Set(finished.map((item) => item.id));
      return;
    }
    const seen = announcedFinishes.current;
    for (const participant of finished) {
      if (seen.has(participant.id)) continue;
      seen.add(participant.id);
      /* Участнику — его собственная печать; ведущий видит её для любого. */
      if (facilitator || participant.id === viewerParticipantId) setSealFor(participant.id);
    }
  }, [session.participants, session.demo, viewerParticipantId, facilitator]);

  /*
    Демо: итог открывается сам, когда партия завершилась, — и при повторном
    входе в завершённую партию тоже. Закрыть его можно без покупки.
  */
  const completed = session.status === 'completed';
  // Срок хранения по политике (пп. 2.6, 6.5): пауза — 45 дней, завершённая — час.
  const deletionAt = sessionDeletionAt(session);
  const [finalOpen, setFinalOpen] = useState(Boolean(demo) && completed);
  const [overviewSector, setOverviewSector] = useState<string | null>(null);
  const wasCompleted = useRef(completed);
  /*
    Партия часто завершается открытием последней карты — в её же окне. Итог
    поверх неё не давал рассмотреть карту: два окна сразу. Поэтому итог ждёт,
    пока окно карты закроют, а в самом окне есть «Перейти к итогу». Признак
    открытого окна панель карт сообщает сразу, в момент открытия, — раньше,
    чем здесь сработает эффект завершения.
  */
  const cardModalOpen = useRef(false);
  const finalPending = useRef(false);
  const onCardModal = (open: boolean) => {
    cardModalOpen.current = open;
    if (!open && finalPending.current) {
      finalPending.current = false;
      setFinalOpen(true);
    }
  };
  useEffect(() => {
    if (demo && completed && !wasCompleted.current) {
      if (cardModalOpen.current) finalPending.current = true;
      else setFinalOpen(true);
    }
    wasCompleted.current = completed;
  }, [demo, completed]);

  const step = demo && demoViewer ? describeDemoStep(session, demoViewer) : null;
  const coach = useDemoCoach(coachKey);
  const viewerSeat = players.find((participant) => participant.id === demoViewer?.participantId);
  const coachId = demo && step && !completed
    ? pickCoach(coach, step.coach, totalResourcesOf(viewerSeat) > 0)
    : null;
  const runDemo = (action: DemoAction) => {
    switch (action.kind) {
      case 'start': actions.start?.(); break;
      case 'seat': actions.seat?.(); break;
      case 'open_story': actions.openStory?.(action.sectorId); break;
      case 'close_story': actions.closeStory?.(); break;
      case 'start_talent': actions.startTalent?.(action.participantId); break;
      case 'draw': actions.draw(action.participantId); break;
      case 'reveal': actions.reveal(action.cardId); break;
      case 'confirm': actions.confirm?.(action.cardId); break;
      case 'resolve': break;
      case 'roll':
        /* Сначала бросок на поле, потом ответ сервера — кубик реагирует на нажатие. */
        dice.start();
        actions.roll(action.participantId);
        break;
    }
  };
  /*
    Пока колонки идут одна под другой, панель хода оказалась бы под полем и
    картами — далеко за краем экрана. В демо следующий шаг — главное, поэтому
    на узком экране он стоит над полем.
  */
  const wideLayout = useMediaQuery('(min-width: 88em)', true);
  const storyCloser = demoViewer && demoViewer.role !== 'guest';
  const storyGuidance = demoStoryGuidance(demoViewer, session.pendingStory?.sectorId ?? null);

  /*
    Removal is not undoable — the participant leaves the roster, their ray is
    freed and given to the next guest. The trash icon sits right beside the
    resource buttons the facilitator presses all game, so it asks first.
  */
  const askRemove = (participantId: string, displayName: string) => {
    modals.openConfirmModal({
      title: 'Удалить участника?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          {displayName} выйдет из партии и из очереди ходов, его луч освободится.
          Отменить это действие нельзя.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Оставить' },
      confirmProps: { color: 'red' },
      onConfirm: () => actions.remove?.(participantId),
    });
  };
  /*
    GAME-31/32: the same two gates the server checks. Showing an enabled button
    that the server will refuse is worse than showing none — the intro and the
    sector story are the moments where the room is meant to be listening.
  */
  const introBlocked = session.phase !== 'playing' || session.pendingStory !== null;
  /*
    Карта события не отпускает ход, пока ведущий не примет ответ (GAME-39).
    Сервер такой бросок отклоняет — значит, и кнопка должна быть закрыта:
    включённая кнопка, которую сервер всё равно отвергнет, выглядит сломанной, а
    на показе именно так и читались зависания хода.
  */
  const awaitingCardDecision = session.drawnCards.some((card) => card.awaitingResolution);
  const canRoll = session.status === 'active' && !introBlocked && pendingCardDraws === 0 &&
    !awaitingCardDecision &&
    Boolean(current) && (facilitator || current?.id === viewerParticipantId);
  const canDraw = session.status === 'active' && session.pendingStory === null &&
    pendingCardDraws > 0 && Boolean(effectParticipant) &&
    (facilitator || effectParticipant?.id === viewerParticipantId);

  return (
    <Stack gap="lg">
      {session.status === 'paused' ? (
        <Alert color="gold" icon={<IconPlayerPause size={18} />} title="Партия на паузе">
          Состояние сохранено. Игровые действия возобновятся после команды ведущего.
          {deletionAt ? ` Продолжить партию можно до ${formatSessionDeletion(deletionAt)} — потом она удаляется.` : ''}
        </Alert>
      ) : null}
      {facilitator && session.status === 'completed' && !demo ? (
        <Alert color="teal" title="Партия завершена">
          Поле, карты, ресурсы и журнал доступны для разбора{deletionAt ? ` до ${formatSessionDeletion(deletionAt)}` : ''}:
          после этого партия удаляется — архив игровых сессий не ведётся.
        </Alert>
      ) : null}
      {demo && completed ? (
        <Alert color="teal" title="Демо завершено" data-testid="demo-completed">
          <Group justify="space-between" gap="sm">
            <Text size="sm">
              Поле, карты и ресурсы можно посмотреть{deletionAt ? ` до ${formatSessionDeletion(deletionAt)}` : ' ещё час'},
              потом демопартия удаляется. Сектора, которые ждут в полной игре, подсвечены на поле.
            </Text>
            <Button size="xs" onClick={() => setFinalOpen(true)} data-testid="demo-final-open">
              {DEMO_FINAL_COPY.reopen}
            </Button>
          </Group>
        </Alert>
      ) : null}

      <MasterSeal opened={sealFor !== null} onClose={() => setSealFor(null)} />

      {demo && demoViewer ? (
        <>
          <DemoFinal
            session={session}
            viewer={demoViewer}
            opened={finalOpen}
            onClose={() => setFinalOpen(false)}
            selectedSector={overviewSector}
            onSelectSector={setOverviewSector}
          />
          <DemoCoach coachId={finalOpen ? null : coachId} coach={coach} />
        </>
      ) : null}

      <GameIntro
        session={session}
        viewerParticipantId={viewerParticipantId}
        facilitator={facilitator}
        busy={busy}
        actions={actions}
        demo={demo ? {
          canClose: Boolean(storyCloser),
          closeLabel: demoViewer?.role === 'solo' ? 'Продолжить' : 'Сказка прочитана',
          guidance: storyGuidance,
        } : null}
      />

      {demo && demoViewer && step && !wideLayout ? (
        <Stack gap="md">
          <DemoTurnPanel
            session={session}
            viewer={demoViewer}
            step={step}
            busy={busy || dice.phase === 'rolling'}
            connection={connection}
            onAction={runDemo}
            onResolve={actions.resolveCard}
            onReplayCoach={coach.reset}
          />
          <DemoGuidance session={session} viewer={demoViewer} />
        </Stack>
      ) : null}

      <Grid gutter="lg" align="flex-start">
        <Grid.Col span={{ base: 12, xl: 8 }}>
          <Stack gap="md">
            <GameBoard
              session={session}
              dice={dice}
              diceVisible={diceVisible}
              overview={demo && completed ? {
                sectors: demo.closedSectors,
                selected: overviewSector,
                onSelect: setOverviewSector,
              } : undefined}
            />
            {demo && completed ? (
              <Paper withBorder p="md">
                <DemoClosedSectors
                  session={session}
                  selected={overviewSector}
                  onSelect={setOverviewSector}
                  hint={DEMO_FINAL_COPY.closedSectorsHintBoard}
                />
              </Paper>
            ) : null}
            <GameCardsPanel
              cards={session.drawnCards}
              players={players}
              viewerParticipantId={viewerParticipantId}
              facilitator={facilitator}
              busy={busy}
              onReveal={actions.reveal}
              onResolve={actions.resolveCard}
              onConfirm={demoViewer?.role === 'solo' ? actions.confirm : undefined}
              allFaces={Boolean(demo)}
              onModalChange={onCardModal}
              modalAction={demo && completed && !finalOpen
                ? { label: DEMO_FINAL_COPY.goToFinal, onClick: () => setFinalOpen(true) }
                : null}
              recommendAward={demo ? (card) => (card.sectorId ? DEMO_SOLO_TASK_REWARDS[card.sectorId] : undefined) : undefined}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, xl: 4 }}>
          <Stack gap="md">
            {demo && demoViewer && step && wideLayout ? (
              <>
                <DemoTurnPanel
                  session={session}
                  viewer={demoViewer}
                  step={step}
                  busy={busy || dice.phase === 'rolling'}
                  connection={connection}
                  onAction={runDemo}
                  onResolve={actions.resolveCard}
                  onReplayCoach={coach.reset}
                />
                <DemoGuidance session={session} viewer={demoViewer} />
              </>
            ) : null}
            {/* GAME-35/36 — подарок и обмен выполняет ведущий за участников. */}
            {facilitator ? (
              <HostActionsPanel session={session} busy={busy} actions={actions} />
            ) : null}
            {demo ? null : <Paper withBorder p="md" data-testid="turn-controls">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text size="xs" tt="uppercase" fw={700} c="var(--accent)">Текущий ход</Text>
                    <Title order={3}>{displayedPlayer?.displayName ?? 'Путь завершён'}</Title>
                    {pendingCardDraws > 0 ? (
                      <Text size="sm" c="var(--text-muted)">
                        {pendingCardDraws > 1 ? 'Тянет карты' : 'Тянет карту'}
                      </Text>
                    ) : null}
                  </div>
                  {session.lastDice ? (
                    <Badge size="xl" variant="light" color="gold" data-testid="last-dice">
                      {session.lastDice.value}
                    </Badge>
                  ) : null}
                </Group>
                {session.lastDice && effectParticipant ? (
                  <Text size="sm" c="var(--text-muted)">
                    {effectParticipant.displayName}: клетка {session.lastDice.from + 1} → {session.lastDice.to + 1}
                  </Text>
                ) : null}
                <Button
                  leftSection={<IconDice size={18} />}
                  disabled={!canRoll || busy || dice.phase === 'rolling'}
                  loading={busy && canRoll}
                  /* Throw first, ask the server second — the cube must react to the press. */
                  onClick={() => {
                    if (!current) return;
                    dice.start();
                    actions.roll(current.id);
                  }}
                >
                  Бросить кубик
                </Button>
                {/*
                  Закрытая кнопка должна объяснять себя: молча неработающая и
                  есть то, что на показе читалось как зависание.
                */}
                {awaitingCardDecision ? (
                  <Text size="xs" c="var(--text-muted)" data-testid="turn-blocked-reason">
                    {facilitator
                      ? 'Ход держит карта события: примите ответ, и бросок откроется.'
                      : 'Ведущий ещё не завершил действие карты события.'}
                  </Text>
                ) : null}
                <Button
                  variant="light"
                  leftSection={<IconCards size={18} />}
                  disabled={!canDraw || busy}
                  onClick={() => effectParticipant && actions.draw(effectParticipant.id)}
                >
                  {pendingCardDraws > 1 ? `Вытянуть карту клетки (${pendingCardDraws})` : 'Вытянуть карту клетки'}
                </Button>
              </Stack>
            </Paper>}

            <Stack gap="sm" data-testid="game-participants" data-coach="resources">
              <Group justify="space-between">
                <Title order={3}>Участники</Title>
                {/* Число — про игроков: ведущий без места за столом в него не входит. */}
                <Badge variant="light" data-testid="game-players-count">
                  {`${PLAYERS_COUNT_LABEL}: ${players.length}`}
                </Badge>
              </Group>
              {players.map((participant) => (
                <ParticipantItem
                  key={participant.id}
                  name={participant.displayName}
                  joinOrder={Math.max(0, participant.joinOrder - 1)}
                  laneColor={participant.laneColor}
                  /*
                    В одиночном демо владельца сажает за стол система, а ведущего
                    в этом сценарии нет: «Ведущий и игрок» там ничего не значит.
                  */
                  isHostSeat={demo?.scenario !== 'solo' && participant.id === session.hostSeatParticipantId}
                  isYou={participant.id === viewerParticipantId}
                  isCurrentTurn={participant.id === displayedTurnId}
                  isConnected={participant.isConnected}
                  trailing={
                    <>
                      <Badge color={participant.finished ? 'teal' : 'gray'} variant="light">
                        {demo
                          ? demoProgressLabel(demo, participant.id, participant.finished)
                          : participant.finished ? 'Финиш' : `Клетка ${(participant.boardPosition ?? 0) + 1}`}
                      </Badge>
                      {participant.finished && !demo ? (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="gold"
                          leftSection={<IconAward size={13} />}
                          onClick={() => setSealFor(participant.id)}
                        >
                          {MASTER_SEAL_TITLE}
                        </Button>
                      ) : null}
                      {/* The ray stays full for the rest of the party, so the fact is shown, not just announced. */}
                      {participant.resourceGoalReachedAt ? (
                        <Badge
                          color="gold"
                          variant="light"
                          leftSection={<IconSunHigh size={12} />}
                          title={goalMessage}
                        >
                          {LANE_CAPACITY} ресурсов
                        </Badge>
                      ) : null}
                      {facilitator && actions.remove ? (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          aria-label={`Удалить участника: ${participant.displayName}`}
                          onClick={() => askRemove(participant.id, participant.displayName)}
                        >
                          <IconTrash size={14} />
                        </Button>
                      ) : null}
                    </>
                  }
                  /*
                    The counters belong to the same card as the name. A finished
                    party keeps nothing for a guest, so `resources` can be absent
                    — then the card is just the roster row it always was.
                  */
                  footer={participant.resources ? (
                    <Group gap={6} wrap="wrap" data-testid={`player-state-${participant.id}`}>
                      {RESOURCE_KEYS.map((resource) => (
                        <div key={resource} data-testid={`resource-${participant.id}-${resource}`}>
                          <ResourceCounter
                            compact
                            resource={resource}
                            value={participant.resources?.[resource] ?? 0}
                            onIncrement={facilitator && actions.resource
                              ? () => actions.resource?.(participant.id, resource, 1)
                              : undefined}
                            onDecrement={facilitator && actions.resource && (participant.resources?.[resource] ?? 0) > 0
                              ? () => actions.resource?.(participant.id, resource, -1)
                              : undefined}
                          />
                        </div>
                      ))}
                      <div data-testid={`money-${participant.id}`}>
                        <MoneyCounter
                          compact
                          value={participant.money ?? 0}
                          onChange={facilitator && actions.money
                            ? (bills) => actions.money?.(participant.id, bills * MONEY_STEP)
                            : undefined}
                        />
                      </div>
                    </Group>
                  ) : undefined}
                />
              ))}
            </Stack>
          </Stack>
        </Grid.Col>

        {/*
          The journal is reference, not control: full width under the play area
          it stays one glance away without pushing the player cards, and the
          board and the turn panel keep the top of the screen to themselves.
        */}
        <Grid.Col span={12}>
          <Paper withBorder p="md">
            <Stack gap="xs">
              <Group gap="xs">
                <IconHistory size={17} color="var(--accent)" />
                <Text fw={700}>Журнал</Text>
              </Group>
              <Divider />
              <ScrollArea.Autosize mah={220}>
                <Stack gap="xs">
                  {session.actions.length === 0 ? (
                    <Text size="sm" c="var(--text-muted)">Действий пока нет.</Text>
                  ) : session.actions.map((action) => (
                    <Text
                      key={action.id}
                      size="sm"
                      c={action.undoneAt ? 'var(--text-subtle)' : 'var(--text-muted)'}
                      td={action.undoneAt ? 'line-through' : undefined}
                    >
                      {action.seq}. {action.summary}
                    </Text>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function totalResourcesOf(participant: { resources: Record<ResourceKey, number> | null } | undefined): number {
  if (!participant?.resources) return 0;
  return RESOURCE_KEYS.reduce((sum, key) => sum + (participant.resources?.[key] ?? 0), 0);
}

/** Демо: сколько секторов маршрута игрок уже закрыл действием карты. */
function demoProgressLabel(
  demo: NonNullable<GameSessionView['demo']>,
  participantId: string,
  finished: boolean,
): string {
  if (finished) return 'Маршрут пройден';
  const done = demo.progress.find((item) => item.participantId === participantId)?.completedSectorIds.length ?? 0;
  return `Секторов: ${done} из ${demo.routeSectorIds.length}`;
}
